import type { FormulaireClient, IdentifiantsClient } from './types'
import { ERREURS_CONTACT, corpsContact, resumeAcces, validerContact, afficherTelephone } from './commun'

/**
 * Validation du formulaire « Nouveau client » — mêmes règles que la console HTML, plus le format de la couleur,
 * et la règle « e-mail OU numéro WhatsApp » (au moins un des deux ; l'e-mail reste recommandé).
 */
export function validerClient(f: FormulaireClient): string | null {
  if (!f.name.trim() || !f.contact_name.trim()) return 'Raison sociale et gérant sont obligatoires.'
  const contact = validerContact({ email: f.email, telephone: f.telephone })
  if (!contact.ok) return contact.message
  if (!/^#[0-9a-fA-F]{6}$/.test(f.brand_color)) return 'Couleur de marque invalide.'
  return null
}

/** Corps de `create-tenant` : `phone` en E.164 + `pays`, `email` seulement s'il est saisi. */
export function corpsCreationClient(f: FormulaireClient): Record<string, unknown> {
  const contact = validerContact({ email: f.email, telephone: f.telephone })
  if (!contact.ok) throw new Error(contact.message)
  return {
    name: f.name.trim(),
    contact_name: f.contact_name.trim(),
    ...corpsContact(contact),
    quartier: f.quartier.trim() || null,
    commune: f.commune.trim() || null,
    plan_tier: f.plan_tier,
    brand_color: f.brand_color,
    white_label: true,
  }
}

/** Réponse de `create-tenant` (corps JSON) → accès à remettre. Sans mot de passe temporaire, jamais « null » affiché. */
export function lireAccesClient(
  rep: {
    email?: string | null; phone?: string | null; temp_password?: string | null; compte_existant?: boolean
    connexion?: string | null; join_code?: string; warning?: string
  },
  f: FormulaireClient,
): IdentifiantsClient {
  const saisi = validerContact({ email: f.email, telephone: f.telephone })
  const email = rep.email ?? (saisi.ok ? saisi.email : null)
  const phone = rep.phone ?? (saisi.ok ? saisi.phone : null)
  const tempPassword = typeof rep.temp_password === 'string' ? rep.temp_password : ''
  const connexion: IdentifiantsClient['connexion'] =
    rep.connexion === 'whatsapp' || rep.connexion === 'existant' || rep.connexion === 'mot_de_passe'
      ? rep.connexion
      : tempPassword ? 'mot_de_passe' : !email && phone ? 'whatsapp' : 'existant'
  return {
    email: email || null,
    phone: phone || null,
    tempPassword,
    compteExistant: rep.compte_existant === true || (tempPassword === '' && connexion !== 'whatsapp'),
    connexion,
    joinCode: rep.join_code ?? '',
    avertissement: rep.warning ?? '',
  }
}

/** Ce que l'écran « Compte créé » et le bouton « Copier les accès » disent de l'identifiant et du secret. */
export function resumeAccesClient(a: IdentifiantsClient): { identifiants: string[]; secret: string | null; phrase: string } {
  const r = resumeAcces({ email: a.email, phone: a.phone, temp_password: a.tempPassword || null, connexion: a.connexion })
  const identifiants = [a.email, a.phone ? afficherTelephone(a.phone) : ''].filter((x): x is string => !!x)
  return { identifiants, secret: r.secret, phrase: r.phrase }
}

const minuscule = (s: string) => s.charAt(0).toLowerCase() + s.slice(1)

const LISIBLE: Record<string, string> = {
  email_already_used: 'cet e-mail a déjà un compte.',
  invalid_email: 'adresse e-mail invalide.',
  compte_autre_role: "cette adresse est déjà utilisée par un compte d'un autre type (équipe, livreur ou marchand). Utilisez une autre adresse.",
  deja_livreur: "ce compte est déjà livreur.",
  deja_gerant: "ce compte gère déjà un autre établissement.",
  /* Connexion par numéro : invalid_phone (le detail du serveur prime), identifiants_differents, phone_already_used, comptes_multiples. */
  ...Object.fromEntries(Object.entries(ERREURS_CONTACT).map(([c, m]) => [c, minuscule(m)])),
  missing_params: 'raison sociale, gérant et un e-mail ou un numéro WhatsApp sont obligatoires.',
  forbidden: "votre compte n'est pas administrateur de la plateforme.",
  unauthorized: 'session expirée — reconnectez-vous.',
  org_failed: "l'organisation n'a pas pu être créée.",
  create_user_failed: "le compte du gérant n'a pas pu être créé.",
  membership_failed: "le rattachement du gérant n'a pas pu être créé (rien n'a été conservé).",
}

/** Traduit le code renvoyé par l'edge function `create-tenant`. */
export function traduireErreurClient(code: string, detail: string, repli: string): string {
  if (code === 'invalid_phone' && detail) return `Création refusée : ${detail}`
  return `Création refusée : ${LISIBLE[code] ?? (detail || code || repli || 'réessayez')}`
}
